import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Switch,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import BadgeIcon from '@mui/icons-material/Badge';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { BusinessUnitEnum } from '../../utils/constants';

const UserRoles = ({ setLoading, setError, setSuccess }) => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [processing, setProcessing] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    userId: null,
    userName: '',
    newRole: ''
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingUser, setSavingUser] = useState(null);
  
  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/users/');
        setUsers(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch users');
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [setLoading, setError]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };
  
  const handleRoleToggleClick = (user) => {
    // Open confirmation dialog
    setConfirmDialog({
      open: true,
      userId: user.id,
      userName: user.name,
      newRole: user.role === 'admin' ? 'employee' : 'admin'
    });
  };
  
  const handleConfirmRoleChange = async () => {
    try {
      const { userId, newRole } = confirmDialog;
      setProcessing(userId);
      
      await axios.put(`/api/users/${userId}`, { role: newRole });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      setSuccess(`User role updated to ${newRole} successfully!`);
      setProcessing(null);
      setConfirmDialog({ ...confirmDialog, open: false });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user role');
      setProcessing(null);
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };
  
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  // Inline editing functions
  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditFormData({
      name: user.name,
      email: user.email,
      business_unit: user.business_unit,
      joining_date: user.joining_date ? new Date(user.joining_date).toISOString().split('T')[0] : ''
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditFormData({});
  };

  const handleSaveEdit = async (userId) => {
    try {
      setSavingUser(userId);
      
      const updateData = { ...editFormData };
      if (updateData.joining_date) {
        updateData.joining_date = new Date(updateData.joining_date).toISOString();
      }
      
      const response = await axios.put(`/api/users/${userId}`, updateData);
      
      // Update local state
      setUsers(users.map(user => (
        user.id === userId ? { ...user, ...response.data } : user
      )));
      
      setSuccess('User information updated successfully!');
      setEditingUser(null);
      setEditFormData({});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user information');
    } finally {
      setSavingUser(null);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value });
  };
  
  // Filter users based on search query
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.business_unit.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Get current page users
  const currentUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            User Role Management
          </Typography>
          
          <TextField
            placeholder="Search users..."
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(0,0,0,0.5)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: '250px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused fieldset': {
                  borderColor: '#702963',
                  boxShadow: '0 0 0 3px rgba(112, 41, 99, 0.2)'
                }
              }
            }}
          />
        </Box>
        
        <TableContainer 
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: '14px',
            background: 'rgba(112, 128, 144, 0.4)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            mb: 2
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Business Unit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                currentUsers.map((user) => (
                  <TableRow 
                    key={user.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(153, 214, 214, 0.08)'
                      }
                    }}
                  >
                    {/* User Name Cell */}
                    <TableCell>
                      {editingUser === user.id ? (
                        <TextField
                          value={editFormData.name || ''}
                          onChange={(e) => handleEditFormChange('name', e.target.value)}
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ) : (
                        user.name
                      )}
                    </TableCell>
                    
                    {/* Email Cell */}
                    <TableCell>
                      {editingUser === user.id ? (
                        <TextField
                          value={editFormData.email || ''}
                          onChange={(e) => handleEditFormChange('email', e.target.value)}
                          type="email"
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ) : (
                        user.email
                      )}
                    </TableCell>
                    
                    {/* Business Unit Cell */}
                    <TableCell>
                      {editingUser === user.id ? (
                        <FormControl size="small" fullWidth>
                          <Select
                            value={editFormData.business_unit || ''}
                            onChange={(e) => handleEditFormChange('business_unit', e.target.value)}
                            sx={{
                              fontSize: '0.875rem'
                            }}
                          >
                            {Object.entries(BusinessUnitEnum).map(([key, value]) => (
                              <MenuItem key={key} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        user.business_unit
                      )}
                    </TableCell>
                    
                    {/* Joining Date Cell */}
                    <TableCell>
                      {editingUser === user.id ? (
                        <TextField
                          type="date"
                          value={editFormData.joining_date || ''}
                          onChange={(e) => handleEditFormChange('joining_date', e.target.value)}
                          size="small"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ) : (
                        user.joining_date ? new Date(user.joining_date).toLocaleDateString() : 'Not set'
                      )}
                    </TableCell>
                    
                    {/* Role Cell */}
                    <TableCell>
                      <Chip
                        label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        variant={user.role === 'admin' ? 'filled' : 'outlined'}
                        size="small"
                        icon={user.role === 'admin' ? <AdminPanelSettingsIcon /> : <BadgeIcon />}
                      />
                    </TableCell>
                    
                    {/* Actions Cell */}
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {editingUser === user.id ? (
                          <>
                            {savingUser === user.id ? (
                              <CircularProgress size={20} sx={{ color: '#009999' }} />
                            ) : (
                              <>
                                <Tooltip title="Save Changes">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveEdit(user.id)}
                                    sx={{ color: '#009999' }}
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    sx={{ color: '#666' }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit User Information">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(user)}
                                sx={{ color: '#009999' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Change to ${user.role === 'admin' ? 'Employee' : 'Admin'}`}>
                              <Box display="inline-block">
                                {processing === user.id ? (
                                  <CircularProgress size={20} sx={{ color: '#009999' }} />
                                ) : (
                                  <Switch
                                    checked={user.role === 'admin'}
                                    onChange={() => handleRoleToggleClick(user)}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#009999',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#009999',
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ 
            '.MuiTablePagination-toolbar': {
              color: 'white'
            },
            '.MuiTablePagination-selectIcon': {
              color: 'white'
            }
          }}
        />
      </motion.div>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: '14px',
            background: 'rgba(127, 140, 156, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle>Confirm Role Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change {confirmDialog.userName}'s role to {confirmDialog.newRole === 'admin' ? 'Admin' : 'Employee'}?
            {confirmDialog.newRole === 'admin' ? 
              ' This will grant them full administrative privileges.' : 
              ' This will remove their administrative privileges.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 3, pr: 3 }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            color="inherit"
            sx={{ borderRadius: '10px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmRoleChange} 
            color="primary" 
            variant="contained"
            sx={{
              borderRadius: '10px',
              backgroundColor: confirmDialog.newRole === 'admin' ? '#702963' : '#009999',
              '&:hover': {
                backgroundColor: confirmDialog.newRole === 'admin' ? '#5a1f50' : '#007f7f'
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoles;
